/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import React from 'react';
import {
  Box,
  FormHelperText,
  InputLabel,
  PopoverOrigin,
  Select,
  SelectProps,
} from '@material-ui/core';
import clsx from 'clsx';
import CircularLoading from 'components/CircularLoading/CircularLoading';
import { ChevronDown, Info } from '../Icons/generated';
import useStyles from './SimpleSelect.styles';

interface SimpleSelectProps extends SelectProps {
  testId: string;
  'aria-label': string;
  onChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  value: unknown;
  error?: boolean;
  size?: 'small' | 'medium';
  children: React.ReactNode;
  helperText?: React.ReactNode;
  renderValue?: (value: unknown) => React.ReactNode;
  resetStyles?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  isLoading?: boolean;
  isScrollable?: boolean;
  isCompact?: boolean;
}

function SimpleSelect(props: SimpleSelectProps) {
  const {
    testId,
    children,
    value,
    size = 'medium',
    error = false,
    helperText,
    resetStyles = false,
    anchorOrigin = {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin,
    renderValue,
    onChange,
    disabled,
    isLoading,
    isScrollable = false,
    startAdornment,
    isCompact = false,
    className,
    'aria-label': ariaLabel,
  } = props;
  const classes = useStyles();

  const CircularLoader = () => (
    <Box className={classes.loadingIcon}>
      <CircularLoading />
    </Box>
  );
  return (
    <Box
      className={clsx({
        [classes.simpleSelect]: true,
        [classes.resetSimpleSelectStyles]: resetStyles,
      })}
    >
      <InputLabel
        htmlFor={`${testId}-native`}
        className={classes.visuallyHidden}
      >
        {ariaLabel}
      </InputLabel>
      <Select
        className={className}
        startAdornment={startAdornment}
        disabled={disabled || isLoading}
        data-cyid={testId}
        data-testid={testId}
        id={`${testId}-select`}
        value={value}
        onChange={onChange}
        disableUnderline
        IconComponent={isLoading ? CircularLoader : ChevronDown}
        variant="outlined"
        aria-label={ariaLabel}
        name={ariaLabel}
        inputProps={{ 'aria-label': ariaLabel, id: `${testId}-native` }}
        MenuProps={{
          PopoverClasses: {
            paper: `${classes.listPaper} ${
              isScrollable ? classes.scrollableList : ''
            } ${startAdornment ? classes.startAdornmentAlignLeft : ''}`,
          },
          getContentAnchorEl: null,
          anchorOrigin,
          transformOrigin,
        }}
        renderValue={renderValue}
        error={error}
        fullWidth
        classes={{
          root: clsx({
            [classes.root]: true,
            [classes.rootSmall]: size === 'small',
            [classes.rootMedium]: size === 'medium',
          }),
          select: classes.select,
          selectMenu: classes.selectMenu,
          disabled: classes.disabled,
          icon: clsx({
            [classes.icon]: true,
            [classes.iconSmall]: size === 'small',
            [classes.iconMedium]: size === 'medium',
          }),
          outlined: clsx({
            [classes.outlined]: true,
            [classes.outlinedSmall]: size === 'small',
            [classes.outlinedMedium]: size === 'medium' && !isCompact,
            [classes.outlinedMediumCompact]: size === 'medium' && isCompact,
          }),
        }}
      >
        {children}
      </Select>
      {helperText && (
        <FormHelperText error={error}>
          <Box display="flex" alignItems="center">
            {error && (
              <Box className={classes.selectInfoIcon}>
                <Info fontSize="inherit" />
              </Box>
            )}
            <Box ml={1}>{helperText}</Box>
          </Box>
        </FormHelperText>
      )}
    </Box>
  );
}

export default SimpleSelect;
